/**
 * NoteListRow Component
 *
 * A single row in the notes list with modern styling,
 * following the PatientListRow design pattern.
 */

import React, { useMemo } from 'react';
import { Avatar } from '@components/primitives/Avatar';
import {
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { Box, Checkbox, Chip, IconButton, LinearProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { UserNoteTypeII } from '../../../types/types';
import { formatDate, truncateTextByCharCount } from '../../../utils/utils';

export interface NoteListRowProps {
  note: UserNoteTypeII;
  onSelect: (note: UserNoteTypeII) => void;
  isSelected?: boolean;
  onToggleSelection?: (noteId: string) => void;
  showCheckbox?: boolean;
  showIssuingUser?: boolean;
  isEhrMode?: boolean;
}

/**
 * Parse patient name into first and last name
 */
function parsePatientName(patientName: string | undefined): { firstName: string; lastName: string } {
  if (!patientName) return { firstName: '', lastName: '' };

  const parts = patientName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };

  // Take first part as first name, rest as last name
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

/**
 * Format relative time for display
 */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now() / 1000;
  const diff = now - timestamp;

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'Yesterday';
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;

  // For older notes, show full date
  return formatDate(timestamp);
}

/**
 * Get status configuration for visual display
 */
function getStatusConfig(
  note: UserNoteTypeII,
  isEhrMode: boolean,
): {
  icon: React.ReactElement;
  label: string;
  color: 'success' | 'warning' | 'info' | 'error';
  progressColor: 'success' | 'warning' | 'info' | 'error';
} | null {
  if (note.status === 'processing') {
    return {
      icon: <SyncIcon sx={{ fontSize: 16 }} />,
      label: 'Processing',
      color: 'warning',
      progressColor: 'warning',
    };
  }

  if (note.status === 'errored') {
    return {
      icon: <ErrorIcon sx={{ fontSize: 16 }} />,
      label: 'Error',
      color: 'error',
      progressColor: 'error',
    };
  }

  if (note.completion_status === 'completed') {
    return {
      icon: <CheckIcon sx={{ fontSize: 16 }} />,
      label: isEhrMode ? (note.is_signed ? 'Signed' : 'Completed') : 'Completed',
      color: 'success',
      progressColor: 'success',
    };
  }

  if (note.completion_status === 'reviewing') {
    return {
      icon: <AssignmentIcon sx={{ fontSize: 16 }} />,
      label: isEhrMode ? (note.is_signed ? 'Amending' : 'Reviewing') : 'Reviewing',
      color: 'info',
      progressColor: 'info',
    };
  }

  return null;
}

export const NoteListRow: React.FC<NoteListRowProps> = React.memo(
  ({
    note,
    onSelect,
    isSelected = false,
    onToggleSelection,
    showCheckbox = true,
    showIssuingUser = false,
    isEhrMode = false,
  }) => {
    const theme = useTheme();

    const displayTitle = useMemo(() => {
      return note.note_title || note.patient_name || 'Untitled Note';
    }, [note.note_title, note.patient_name]);

    const relativeTime = useMemo(() => formatRelativeTime(note.time_uploaded), [note.time_uploaded]);

    const statusConfig = useMemo(() => getStatusConfig(note, isEhrMode), [note, isEhrMode]);

    const summaryPreview = useMemo(() => {
      if (note.status === 'errored') {
        return 'There was an error generating this note.';
      }
      return truncateTextByCharCount(note.summary, 120);
    }, [note.summary, note.status]);

    const patientNameParts = useMemo(() => parsePatientName(note.patient_name), [note.patient_name]);
    const hasPatient = Boolean(note.patient_id);

    const handleClick = (e: React.MouseEvent) => {
      // Don't navigate if clicking the checkbox
      if ((e.target as HTMLElement).closest('.note-checkbox')) return;
      if (note.status !== 'errored') {
        onSelect(note);
      }
    };

    const handleCheckboxClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleSelection?.(note.note_id);
    };

    const isClickable = note.status !== 'errored';

    return (
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          px: 3,
          py: 2.5,
          cursor: isClickable ? 'pointer' : 'default',
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
          transition: 'background-color 0.15s ease',
          '&:hover': isClickable
            ? {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              }
            : undefined,
          '&:last-child': {
            borderBottom: 'none',
          },
          opacity: note.status === 'errored' ? 0.7 : 1,
        }}
      >
        {/* Checkbox */}
        {showCheckbox && (
          <Box
            className="note-checkbox"
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'center',
            }}
          >
            <Checkbox
              checked={isSelected}
              onClick={handleCheckboxClick}
              size="small"
              sx={{
                p: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                },
              }}
            />
          </Box>
        )}

        {/* Avatar - Show patient avatar if patient_id exists, otherwise spacer for alignment */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'center',
            width: 44,
            height: 44,
          }}
        >
          {hasPatient && (
            <Avatar
              id={note.patient_id}
              firstName={patientNameParts.firstName}
              lastName={patientNameParts.lastName}
              sx={{
                width: 44,
                height: 44,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {displayTitle}
            </Typography>

            {/* Status Chip */}
            {statusConfig && (
              <Chip
                icon={statusConfig.icon}
                label={statusConfig.label}
                size="small"
                color={statusConfig.color}
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.6875rem',
                  fontWeight: 500,
                  flexShrink: 0,
                  '& .MuiChip-icon': {
                    marginLeft: '6px',
                    marginRight: '-2px',
                  },
                }}
              />
            )}
          </Box>

          {/* Patient Name (if different from title) and Issuing User */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.75 }}>
            {note.patient_name && note.note_title && note.patient_name !== note.note_title && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 14, color: theme.palette.grey[400] }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  {note.patient_name}
                </Typography>
              </Box>
            )}

            {showIssuingUser && note.issuing_user_name && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                {note.issuing_user_name}
              </Typography>
            )}
          </Box>

          {/* Summary Preview */}
          <Typography
            variant="body2"
            sx={{
              color: note.status === 'errored' ? theme.palette.error.main : theme.palette.text.secondary,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.5,
            }}
          >
            {summaryPreview}
          </Typography>

          {/* Progress Bar for Processing */}
          {note.status === 'processing' && (
            <Box sx={{ mt: 1.5, maxWidth: 200 }}>
              <LinearProgress
                color="warning"
                sx={{
                  height: 3,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                }}
              />
            </Box>
          )}
        </Box>

        {/* Right Side: Time and Arrow */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            flexShrink: 0,
            gap: 0.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.8125rem',
              whiteSpace: 'nowrap',
            }}
          >
            {relativeTime}
          </Typography>

          {isClickable && (
            <IconButton
              size="small"
              sx={{
                color: theme.palette.grey[400],
                p: 0.5,
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  },
);

NoteListRow.displayName = 'NoteListRow';
